/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useCallback } from 'react';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import type { NavSubmenuMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { L1ExpandableMenuItemSubmenu } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/l1-expandable-menu-item-submenu/main.tsx';
import {
	useIsNavigationLoading,
	useIsNavigationLocked,
	useIsNavigationInitialized,
} from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { JpdNavViewsContainer } from '../controllers/index.tsx';
import type {
	ContainerTypePermissions,
	GetViewUrl,
	UseIsReadOnly,
	ViewsEntityLimitType,
	ViewsetsEntityLimitType,
} from '../controllers/types.tsx';
import type {
	UseAnalyticsL2,
	UseAnalyticsL3,
	UseAnalyticsL4,
} from '../controllers/analytics/types.tsx';
import { useContainerId } from '../controllers/context.tsx';
import type { NavigationContainer } from '../common/types.tsx';
import { MenuContainer } from './menu-container/index.tsx';
import { MenuItems } from './menu-items/index.tsx';

export type JpdNavViewsMainProps = {
	navigationContainer: NavigationContainer;
	forceLoading?: boolean;
	dragDropEnabled?: boolean;
	renderComponentAfter?: (
		isNavigationInitialized: boolean,
		isNavigationLoading: boolean,
	) => React.ReactNode;
};

const JpdNavViewsMain = memo(
	({
		navigationContainer,
		forceLoading,
		dragDropEnabled,
		renderComponentAfter,
	}: JpdNavViewsMainProps) => {
		const containerId = useContainerId();
		const [isNavigationLoading] = useIsNavigationLoading({ containerId });
		const [isNavigationLocked] = useIsNavigationLocked({ containerId });
		const [isNavigationInitialized] = useIsNavigationInitialized({
			containerId,
		});

		const handleFetch = useCallback(() => {
			return Promise.resolve();
		}, []);

		const menuItems = useCallback<NavSubmenuMenuItems>(() => {
			return (
				<MenuItems navigationContainer={navigationContainer} dragDropEnabled={dragDropEnabled} />
			);
		}, [navigationContainer, dragDropEnabled]);

		const isFetching = forceLoading || isNavigationLoading || !isNavigationInitialized;

		return (
			<>
				<MenuListItem>
					<MenuContainer isLocked={isNavigationLocked}>
						<MenuList>
							<L1ExpandableMenuItemSubmenu
								fetch={handleFetch}
								isFetching={isFetching}
								MenuItems={menuItems}
							/>
							{isFetching && <UFOLoadHold name="jpd.nav4-menu-loading" />}
						</MenuList>
					</MenuContainer>
				</MenuListItem>
				{renderComponentAfter && renderComponentAfter(isNavigationInitialized, isFetching)}
			</>
		);
	},
);

type JpdNavViewsProps<T extends NavigationContainer> = {
	name?: string;
	container: T;
	sectionsEnabled: boolean;
	containerTypePermissions: ContainerTypePermissions;
	dragDropEnabled?: boolean;
	forceLoading?: boolean;
	viewsEntityLimitType: ViewsEntityLimitType;
	viewsetsEntityLimitType?: ViewsetsEntityLimitType;
	renderComponentAfter?: (
		isNavigationInitialized: boolean,
		isNavigationLoading: boolean,
	) => React.ReactNode;
	getViewUrl: GetViewUrl<T>;
	useIsReadOnly?: UseIsReadOnly<T>;
	useAnalyticsL2: UseAnalyticsL2<T>;
	useAnalyticsL3: UseAnalyticsL3<T>;
	useAnalyticsL4: UseAnalyticsL4<T>;
};

export const JpdNavViews = <T extends NavigationContainer>({
	name,
	container,
	dragDropEnabled,
	sectionsEnabled,
	containerTypePermissions,
	forceLoading,
	viewsEntityLimitType,
	viewsetsEntityLimitType,
	renderComponentAfter,
	getViewUrl,
	useIsReadOnly,
	useAnalyticsL2,
	useAnalyticsL3,
	useAnalyticsL4,
}: JpdNavViewsProps<T>): React.JSX.Element => {
	return (
		<JpdNavViewsContainer
			name={name}
			container={container}
			viewsEntityLimitType={viewsEntityLimitType}
			viewsetsEntityLimitType={viewsetsEntityLimitType}
			sectionsEnabled={sectionsEnabled}
			containerTypePermissions={containerTypePermissions}
			getViewUrl={getViewUrl}
			useIsReadOnly={useIsReadOnly}
			useAnalyticsL2={useAnalyticsL2}
			useAnalyticsL3={useAnalyticsL3}
			useAnalyticsL4={useAnalyticsL4}
		>
			<JpdNavViewsMain
				navigationContainer={container}
				dragDropEnabled={dragDropEnabled}
				forceLoading={forceLoading}
				renderComponentAfter={renderComponentAfter}
			/>
		</JpdNavViewsContainer>
	);
};
